/**
 * ComplianceManagerPage - Vue consolidee de TOUS les controles de conformite
 * NIS2 / CyFun / ISO 27001, decomposes en 2 sous-onglets :
 *   - Auto (M365)              : les 107 controles M365 audites automatiquement
 *   - Manuel (Gouvernance/Org) : les ~45 controles gouvernance/organisation
 *                                non auditables techniquement (politique, RH,
 *                                supply chain, etc.)
 *
 * Le score consolide en haut combine les 2 vues pour donner la posture
 * compliance globale du tenant - utile pour audit externe / rapport conseil
 * d administration.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card, Title2, Title3, Body1, Caption1, Button, Badge, Spinner,
  TabList, Tab, type SelectTabData, type SelectTabEvent,
  Dropdown, Option, Input,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  MessageBar, MessageBarBody, MessageBarTitle,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Textarea, Field,
  tokens,
} from '@fluentui/react-components';
import {
  ShieldCheckmark24Regular, CheckmarkCircle20Filled, DismissCircle20Filled,
  Circle20Regular, ArrowSync20Regular,
  Attach20Regular, Delete20Regular, ArrowDownload20Regular,
  ClipboardTaskListLtr20Regular,
  PuzzlePiece20Regular,
  Search20Regular, Wrench20Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader';
import { FamilyBadge, SeverityBadge, StatusBadge } from '../components/StatusBadge';
import { FrameworkInlineText } from '../components/FrameworkBadge';
import { BrandIcon } from '../components/BrandIcon';
import { CustomControlsTab } from '../components/compliance/CustomControlsTab';
import { ManualChecksPage } from './ManualChecksPage';
import { azureConnectionsApi } from '../api/azureConnectionsApi';
import { azureScansApi } from '../api/azureScansApi';
import { useAuth } from '../auth/AuthContext';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { useLatestScan } from '../hooks/useScans';
import { controls } from '../domain/controls';
import { AZURE_CONTROLS } from '../domain/azureControls';
import {
  MANUAL_GOVERNANCE_CONTROLS,
  GOVERNANCE_FAMILY_ORDER,
  GOVERNANCE_FAMILY_COLORS,
  type GovernanceFamily,
  type GovernanceControlDefinition,
} from '../domain/manualControls';
import {
  manualDecisionsApi,
  type ManualDecision,
  type ManualDecisionValue,
} from '../api/manualDecisionsApi';

// v2026.06.120 - decomposition de 'auto' en 'auto-m365' + 'auto-azure',
// nouveau tab 'custom' pour les controles personnalises de l auditeur.
type TabKey = 'global' | 'auto-m365' | 'auto-azure' | 'manual' | 'manual-tech' | 'custom';

// =============================================================================
//  EVIDENCE / ATTACHMENT MODEL (backward compatible)
// =============================================================================
// L'evidence est stockee comme chaine dans la decision. Pour supporter les
// pieces jointes sans casser les anciennes decisions text-only, on encode en
// JSON avec un marqueur _kind='nis365-evidence-v2'. Le parser distingue :
//   - chaine simple   -> text-only (legacy)
//   - JSON v2          -> { text, attachment }

interface EvidenceAttachment {
  name:    string;
  type:    string;        // MIME type
  size:    number;        // bytes raw
  dataUrl: string;        // "data:image/png;base64,...."
}

interface ParsedEvidence {
  text?:       string;
  attachment?: EvidenceAttachment;
}

/** Limite de taille raw (avant base64) pour une piece jointe inline. */
const ATTACHMENT_MAX_BYTES = 500 * 1024; // 500 Ko

function parseEvidence(evidence?: string): ParsedEvidence {
  if (!evidence) return {};
  // Format v2 ? Detection par le prefixe JSON _kind
  if (evidence.startsWith('{"_kind":"nis365-evidence-v2"')) {
    try {
      const p = JSON.parse(evidence) as { text?: string; attachment?: EvidenceAttachment };
      return { text: p.text, attachment: p.attachment };
    } catch { /* fallthrough text only */ }
  }
  // Legacy : tout est text
  return { text: evidence };
}

function serializeEvidence(text: string | undefined, attachment: EvidenceAttachment | undefined): string | undefined {
  const cleanText = text?.trim() || undefined;
  if (!cleanText && !attachment) return undefined;
  if (!attachment) return cleanText;
  return JSON.stringify({ _kind: 'nis365-evidence-v2', text: cleanText, attachment });
}

async function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

export function ComplianceManagerPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { tenant } = useActiveTenant();
  const scan = useLatestScan(tenant.id);
  const canEdit = user?.role === 'admin' || user?.role === 'auditor';

  const navigate = useNavigate();
  const [activeTab, setActiveTab]   = useState<TabKey>('global');
  const manualTechCount = useMemo(() => scan?.results.filter((r) => r.status === 'manual').length ?? 0, [scan]);
  // v2026.06.169 - Compteur Custom controls (lu depuis localStorage du tenant courant)
  const [customCount, setCustomCount] = useState(0);
  useEffect(() => {
    const compute = () => {
      try {
        const raw = window.localStorage.getItem('nis365-custom-controls-' + tenant.id);
        const arr = raw ? JSON.parse(raw) : [];
        setCustomCount(Array.isArray(arr) ? arr.length : 0);
      } catch { setCustomCount(0); }
    };
    compute();
    // Recompute si l'user change de tab (le localStorage peut avoir bougé)
    const id = window.setInterval(compute, 5000);
    return () => window.clearInterval(id);
  }, [tenant.id, activeTab]);
  const [decisions, setDecisions]   = useState<Record<string, ManualDecision>>({});
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [editing, setEditing]       = useState<GovernanceControlDefinition | null>(null);
  const [editDecision, setEditDecision] = useState<ManualDecisionValue>('pending');
  const [editEvidence, setEditEvidence] = useState('');
  const [editAttachment, setEditAttachment] = useState<EvidenceAttachment | undefined>(undefined);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [saving, setSaving]         = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    void loadDecisions();
  }, []);

  async function loadDecisions() {
    setLoading(true);
    setError(null);
    try {
      const res = await manualDecisionsApi.list();
      setDecisions(res.decisions ?? {});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  // -------------------- Score consolide --------------------
  // v2026.06.170 - Le score AUTO inclut maintenant M365 + Azure (cohérence Vue globale)

  // Azure scan (1ère connexion) - alimente le score auto
  const azureConnQHero = useQuery({ queryKey: ['azure-connections'], queryFn: () => azureConnectionsApi.list(), staleTime: 60_000, retry: false });
  const azureConnIdHero = azureConnQHero.data?.connections?.[0]?.id;
  const azureScanQHero = useQuery({
    queryKey: ['azure-latest', azureConnIdHero],
    queryFn:  () => azureScansApi.latest(azureConnIdHero!),
    enabled:  !!azureConnIdHero, staleTime: 10_000, retry: false,
  });

  // Score auto : moyenne pondérée score M365 + Azure (chaque contrôle pesant 1)
  const autoScore = useMemo(() => {
    const scoredM365 = (scan?.results ?? []).filter((r) => r.score != null && r.status !== 'manual' && r.status !== 'not_applicable');
    const scoredAzure = (azureScanQHero.data?.results ?? []).filter((r) => (r as { score?: number }).score != null && r.status !== 'manual' && r.status !== 'not_applicable');
    const totalCount = scoredM365.length + scoredAzure.length;
    if (totalCount === 0) return scan ? 0 : null;
    const sumM365  = scoredM365.reduce((s, r) => s + (r.score ?? 0), 0);
    const sumAzure = scoredAzure.reduce((s, r) => s + ((r as { score?: number }).score ?? 0), 0);
    return Math.round(100 * (sumM365 + sumAzure) / totalCount);
  }, [scan, azureScanQHero.data]);

  // Compteur affiché en sous-titre de la card SCORE AUTO
  const autoScannedCount = useMemo(() => {
    const m365Count  = (scan?.results ?? []).filter((r) => r.status !== 'manual' && r.status !== 'not_applicable').length;
    const azureCount = (azureScanQHero.data?.results ?? []).filter((r) => r.status !== 'manual' && r.status !== 'not_applicable').length;
    return m365Count + azureCount;
  }, [scan, azureScanQHero.data]);

  // Manuel : score base sur les decisions sur les controles gouvernance
  const manualScore = useMemo(() => {
    const total = MANUAL_GOVERNANCE_CONTROLS.length;
    if (total === 0) return 0;
    const conformes = MANUAL_GOVERNANCE_CONTROLS.filter((c) => decisions[c.id]?.decision === 'conforme').length;
    return Math.round(100 * conformes / total);
  }, [decisions]);

  // Global : moyenne ponderee par nombre de controles M365 + Azure + Manuel
  const globalScore = useMemo(() => {
    if (autoScore == null) return manualScore;
    const wAuto   = controls.length + AZURE_CONTROLS.length;
    const wManual = MANUAL_GOVERNANCE_CONTROLS.length;
    return Math.round((autoScore * wAuto + manualScore * wManual) / (wAuto + wManual));
  }, [autoScore, manualScore]);

  // -------------------- Edit dialog --------------------
  function openEdit(c: GovernanceControlDefinition) {
    setEditing(c);
    const current = decisions[c.id];
    const parsed = parseEvidence(current?.evidence);
    setEditDecision(current?.decision ?? 'pending');
    setEditEvidence(parsed.text ?? '');
    setEditAttachment(parsed.attachment);
    setAttachmentError(null);
  }

  function closeEdit() {
    setEditing(null);
    setAttachmentError(null);
  }

  async function onPickFile(file: File | null) {
    setAttachmentError(null);
    if (!file) return;
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setAttachmentError(t('complianceManager.dialog.attachmentTooLarge', { maxKb: ATTACHMENT_MAX_BYTES / 1024 }));
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setEditAttachment({
        name:    file.name,
        type:    file.type || 'application/octet-stream',
        size:    file.size,
        dataUrl,
      });
    } catch (e) {
      setAttachmentError((e as Error).message);
    }
  }

  function removeAttachment() {
    setEditAttachment(undefined);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function saveDecision() {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      const evidence = serializeEvidence(editEvidence, editAttachment);
      const res = await manualDecisionsApi.upsert({
        controlId: editing.id,
        decision:  editDecision,
        evidence,
      });
      setDecisions((prev) => {
        const next = { ...prev };
        if (editDecision === 'pending') delete next[editing.id];
        else                            next[editing.id] = res;
        return next;
      });
      closeEdit();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader
        title={t('complianceManager.title')}
        description={t('complianceManager.subtitle')}
      />

      {/* Score consolide hero */}
      <ScoreHero
        global={globalScore}
        auto={autoScore}
        manual={manualScore}
        autoCount={autoScannedCount}
        manualCount={MANUAL_GOVERNANCE_CONTROLS.length}
        manualDecided={MANUAL_GOVERNANCE_CONTROLS.filter((c) => decisions[c.id]?.decision === 'conforme').length}
      />

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16, marginTop: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* v2026.06 - Tabs en style "boutons" pour plus de visibilite (au lieu
          de l onglet souligne par defaut). Pattern Pivot-like : pill rounded,
          fond accent sur le tab actif. */}
      <div style={{ marginTop: 24 }}>
        <div style={{
          display: 'inline-flex',
          gap: 6,
          padding: 4,
          background: tokens.colorNeutralBackground2,
          border: `1px solid ${tokens.colorNeutralStroke2}`,
          borderRadius: 8,
          marginBottom: 8,
        }}>
          {([
            { key: 'global',     label: t('complianceManager.tabs.global'),    count: controls.length + AZURE_CONTROLS.length + MANUAL_GOVERNANCE_CONTROLS.length + customCount, icon: <CheckmarkCircle20Filled primaryFill="currentColor" /> },
            { key: 'auto-m365',  label: t('complianceManager.tabs.autoM365'),  count: controls.length,               icon: <BrandIcon name="m365"  size={16} /> },
            { key: 'auto-azure', label: t('complianceManager.tabs.autoAzure'), count: AZURE_CONTROLS.length,         icon: <BrandIcon name="azure" size={16} /> },
            { key: 'manual',     label: t('complianceManager.tabs.manual'),    count: MANUAL_GOVERNANCE_CONTROLS.length, icon: <ClipboardTaskListLtr20Regular /> },
            { key: 'manual-tech', label: t('complianceManager.tabs.manualTech', 'Manuel (Technique)'), count: manualTechCount, icon: <Wrench20Regular /> },
            { key: 'custom',     label: t('complianceManager.tabs.custom'),    count: customCount,                    icon: <PuzzlePiece20Regular /> },
          ] as Array<{ key: TabKey; label: string; count: number; icon: React.ReactNode }>).map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '8px 16px',
                  border: 'none',
                  background: isActive ? tokens.colorBrandBackground : 'transparent',
                  color: isActive ? tokens.colorNeutralForegroundOnBrand : tokens.colorNeutralForeground1,
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: isActive ? 600 : 500,
                  fontSize: 14,
                  transition: 'background 0.1s ease',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = tokens.colorNeutralBackground3; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center',
                  width: 18, height: 18,
                  color: isActive ? tokens.colorNeutralForegroundOnBrand : tokens.colorNeutralForeground2,
                }}>{tab.icon}</span>
                {tab.label}
                {tab.count > 0 && (
                  <Badge size="small" appearance="filled" style={{
                    background: isActive ? 'rgba(255,255,255,0.25)' : tokens.colorBrandBackground2,
                    color: isActive ? '#fff' : tokens.colorBrandForeground1,
                    fontWeight: 700,
                  }}>{tab.count}</Badge>
                )}
              </button>
            );
          })}
        </div>
        {/* TabList Fluent gardée invisible pour la compat A11y (focus management).
            Si tu preferes la retirer completement, decommente l import + cleanup. */}
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_: SelectTabEvent, d: SelectTabData) => setActiveTab(d.value as TabKey)}
          style={{ display: 'none' }}
        >
          <Tab value="global">{t('complianceManager.tabs.global')}</Tab>
          <Tab value="auto-m365">{t('complianceManager.tabs.autoM365')}</Tab>
          <Tab value="auto-azure">{t('complianceManager.tabs.autoAzure')}</Tab>
          <Tab value="manual">{t('complianceManager.tabs.manual')}</Tab>
          <Tab value="custom">{t('complianceManager.tabs.custom')}</Tab>
        </TabList>

        {activeTab === 'global' && (
          <div style={{ marginTop: 16 }}>
            {loading ? (
              <Spinner label={t('complianceManager.loading')} />
            ) : (
              <GlobalTab
                scan={scan}
                decisions={decisions}
                onEditManual={openEdit}
                onNavigateAuto={(id) => navigate(`/controls/${id}`)}
                canEdit={canEdit}
              />
            )}
          </div>
        )}

        {activeTab === 'auto-m365' && (
          <div style={{ marginTop: 16 }}>
            <AutoTab
              scan={scan}
              onNavigate={(id) => navigate(`/controls/${id}`)}
            />
          </div>
        )}

        {activeTab === 'auto-azure' && (
          <div style={{ marginTop: 16 }}>
            <AutoAzureTab onNavigate={(id) => navigate(`/azure/${id}`)} onGoFull={() => navigate('/azure')} />
          </div>
        )}

        {activeTab === 'manual-tech' && (
          <div style={{ marginTop: 16 }}>
            <ManualChecksPage embedded />
          </div>
        )}

        {activeTab === 'custom' && (
          <div style={{ marginTop: 16 }}>
            <CustomControlsTab tenantId={tenant.id} canEdit={canEdit} />
          </div>
        )}

        {activeTab === 'manual' && (
          <div style={{ marginTop: 16 }}>
            {loading ? (
              <Spinner label={t('complianceManager.loading')} />
            ) : (
              <ManualTab
                decisions={decisions}
                onEdit={openEdit}
                canEdit={canEdit}
              />
            )}
          </div>
        )}
      </div>

      {/* Dialog edit decision */}
      {editing && (
        <Dialog open={true} onOpenChange={(_, data) => { if (!data.open) closeEdit(); }}>
          <DialogSurface style={{ maxWidth: 640 }}>
            <DialogBody>
              <DialogTitle>{editing.id} — {editing.title}</DialogTitle>
              <DialogContent>
                <Body1 style={{ marginBottom: 12, color: tokens.colorNeutralForeground2 }}>
                  {editing.description}
                </Body1>
                <Caption1 block style={{ marginBottom: 16, color: tokens.colorNeutralForeground3 }}>
                  {editing.rationale}
                </Caption1>

                <Field label={t('complianceManager.dialog.decisionLabel')} required>
                  <Dropdown
                    value={t(`complianceManager.decision.${editDecision}`)}
                    selectedOptions={[editDecision]}
                    onOptionSelect={(_, d) => setEditDecision(d.optionValue as ManualDecisionValue)}
                  >
                    <Option value="pending">{t('complianceManager.decision.pending')}</Option>
                    <Option value="conforme">{t('complianceManager.decision.conforme')}</Option>
                    <Option value="fail">{t('complianceManager.decision.fail')}</Option>
                    <Option value="skip">{t('complianceManager.decision.skip')}</Option>
                  </Dropdown>
                </Field>

                <Field
                  label={t('complianceManager.dialog.evidenceLabel')}
                  hint={t('complianceManager.dialog.evidenceHint')}
                  style={{ marginTop: 12 }}
                >
                  <Textarea
                    rows={4}
                    value={editEvidence}
                    onChange={(_, d) => setEditEvidence(d.value)}
                    placeholder={editing.remediation}
                  />
                </Field>

                {/* Attachment upload (image, PDF, ...). Limite 500Ko. */}
                <Field
                  label={t('complianceManager.dialog.attachmentLabel')}
                  hint={t('complianceManager.dialog.attachmentHint', { maxKb: ATTACHMENT_MAX_BYTES / 1024 })}
                  style={{ marginTop: 12 }}
                  validationState={attachmentError ? 'error' : 'none'}
                  validationMessage={attachmentError ?? undefined}
                >
                  {editAttachment ? (
                    <AttachmentPreview
                      attachment={editAttachment}
                      onRemove={removeAttachment}
                      readOnly={!canEdit}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf,.txt,.csv,.docx,.xlsx"
                        onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
                        style={{ display: 'none' }}
                      />
                      <Button
                        appearance="secondary"
                        icon={<Attach20Regular />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!canEdit}
                      >
                        {t('complianceManager.dialog.attachmentPick')}
                      </Button>
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                        {t('complianceManager.dialog.attachmentAccepted')}
                      </Caption1>
                    </div>
                  )}
                </Field>

                <Caption1 block style={{ marginTop: 12, color: tokens.colorNeutralForeground3 }}>
                  <strong>{t('complianceManager.dialog.remediation')}:</strong> {editing.remediation}
                </Caption1>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={closeEdit} disabled={saving}>
                  {t('complianceManager.dialog.cancel')}
                </Button>
                <Button appearance="primary" onClick={saveDecision} disabled={saving || !canEdit}>
                  {saving ? <Spinner size="tiny" /> : t('complianceManager.dialog.save')}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
}

// ============================================================================
//                              SCORE HERO
// ============================================================================
function ScoreHero({ global, auto, manual, autoCount, manualCount, manualDecided }: {
  global: number; auto: number | null; manual: number;
  autoCount: number; manualCount: number; manualDecided: number;
}) {
  const { t } = useTranslation();
  const color = global >= 80 ? '#1F7A45' : global >= 60 ? '#A86317' : '#B82329';
  return (
    <Card style={{ padding: 24, borderLeft: `4px solid ${color}`, marginTop: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
        <ScoreBlock
          label={t('complianceManager.hero.auto')}
          score={auto}
          sub={t('complianceManager.hero.autoSub', { count: autoCount })}
        />
        <ScoreBlock
          label={t('complianceManager.hero.manual')}
          score={manual}
          sub={t('complianceManager.hero.manualSub', { decided: manualDecided, total: manualCount })}
        />
        <ScoreBlock
          label={t('complianceManager.hero.global')}
          score={global}
          sub={t('complianceManager.hero.globalSub')}
          highlight
          color={color}
        />
      </div>
    </Card>
  );
}

function ScoreBlock({ label, score, sub, highlight, color }: {
  label: string; score: number | null; sub: string; highlight?: boolean; color?: string;
}) {
  return (
    <div>
      <Caption1 style={{
        textTransform: 'uppercase', letterSpacing: 0.5,
        fontSize: 10, color: tokens.colorNeutralForeground3,
        fontWeight: highlight ? 700 : 500,
      }}>
        {label}
      </Caption1>
      <div style={{
        fontSize: highlight ? 40 : 28,
        fontWeight: 700,
        lineHeight: 1.1,
        color: color ?? tokens.colorNeutralForeground1,
        marginTop: 4,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {score == null ? '—' : score}
        <span style={{ fontSize: highlight ? 18 : 14, color: tokens.colorNeutralForeground3, fontWeight: 400 }}>/100</span>
      </div>
      <Caption1 style={{ display: 'block', marginTop: 4, color: tokens.colorNeutralForeground3 }}>
        {sub}
      </Caption1>
    </div>
  );
}

// ============================================================================
//                              AUTO TAB (M365)
// ============================================================================
// =============================================================================
//  AUTO AZURE TAB - placeholder pointant vers la page NIS Azure dediee.
//  v2026.06.120 : pour l instant, on affiche un resume du catalogue + CTA vers
//  /azure. Plus tard, on pourra integrer un useLatestAzureScan() comme M365.
// =============================================================================
function AutoAzureTab({ onNavigate, onGoFull }: { onNavigate: (id: string) => void; onGoFull: () => void }) {
  const { t } = useTranslation();

  const connQ = useQuery({ queryKey: ['azure-connections'], queryFn: () => azureConnectionsApi.list(), staleTime: 60_000, retry: false });
  const connId = connQ.data?.connections?.[0]?.id;
  const scanQ = useQuery({ queryKey: ['azure-latest', connId], queryFn: () => azureScansApi.latest(connId!), enabled: !!connId, staleTime: 10_000, retry: false });
  const statusById = useMemo(() => {
    const m = new Map<string, string>();
    (scanQ.data?.results ?? []).forEach((r) => m.set(r.controlId, r.status));
    return m;
  }, [scanQ.data]);

  // v2026.06.168 - Filtres par colonne (style Excel)
  const [fId, setFId]               = useState('');
  const [fFamily, setFFamily]       = useState<string>('all');
  const [fTitle, setFTitle]         = useState('');
  const [fSeverity, setFSeverity]   = useState<string>('all');
  const [fStatus, setFStatus]       = useState<string>('all');
  const [fReference, setFReference] = useState('');
  const allFamilies = useMemo(() => Array.from(new Set(AZURE_CONTROLS.map((c) => c.family))).sort(), []);
  const activeFilters = (fId ? 1 : 0) + (fFamily !== 'all' ? 1 : 0) + (fTitle ? 1 : 0) + (fSeverity !== 'all' ? 1 : 0) + (fStatus !== 'all' ? 1 : 0) + (fReference ? 1 : 0);
  const resetAll = () => { setFId(''); setFFamily('all'); setFTitle(''); setFSeverity('all'); setFStatus('all'); setFReference(''); };

  const rows = useMemo(() => {
    return AZURE_CONTROLS.filter((c) => {
      if (fId && !c.id.toLowerCase().includes(fId.toLowerCase())) return false;
      if (fFamily !== 'all' && c.family !== fFamily) return false;
      if (fTitle && !c.title.toLowerCase().includes(fTitle.toLowerCase())) return false;
      if (fSeverity !== 'all' && c.severity !== fSeverity) return false;
      if (fStatus !== 'all') {
        const st = statusById.get(c.id);
        if (fStatus === 'not-scanned') { if (st) return false; }
        else if (st !== fStatus) return false;
      }
      if (fReference && !c.references.some((r) => `${r.framework} ${r.ref} ${r.label}`.toLowerCase().includes(fReference.toLowerCase()))) return false;
      return true;
    });
  }, [fId, fFamily, fTitle, fSeverity, fStatus, fReference, statusById]);

  return (
    <Card>
      <style>{`
        tr[data-nis365-clickable-row] { cursor: pointer; transition: background-color 0.12s ease; }
        tr[data-nis365-clickable-row]:hover { background-color: ${tokens.colorNeutralBackground1Hover}; }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
        <BrandIcon name="azure" size={22} />
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{rows.length} / {AZURE_CONTROLS.length}</Caption1>
        {activeFilters > 0 && (
          <Button size="small" appearance="subtle" onClick={resetAll}>Réinitialiser les filtres ({activeFilters})</Button>
        )}
        <Button appearance="subtle" size="small" onClick={onGoFull} style={{ marginLeft: 'auto' }}>
          {t('complianceManager.autoAzure.cta', 'Scanner via NIS Azure →')}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>{t('controls.list.id')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.table.family')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.list.control')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.table.severity')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.filter.status')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.list.references')}</TableHeaderCell>
          </TableRow>
          {/* v2026.06.168 - filtres par colonne */}
          <TableRow>
            <TableHeaderCell><Input size="small" placeholder="ID…" value={fId} onChange={(_, d) => setFId(d.value)} style={{ width: '100%', minWidth: 90 }} /></TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Toutes" value={fFamily === 'all' ? 'Toutes' : fFamily} selectedOptions={[fFamily]}
                onOptionSelect={(_, d) => setFFamily(d.optionValue ?? 'all')} style={{ minWidth: 110 }}>
                <Option value="all">Toutes</Option>
                {allFamilies.map((f) => <Option key={f} value={f}>{f}</Option>)}
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell><Input size="small" placeholder="Rechercher titre…" value={fTitle} contentBefore={<Search20Regular />} onChange={(_, d) => setFTitle(d.value)} style={{ width: '100%' }} /></TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Toutes" value={fSeverity === 'all' ? 'Toutes' : t(`severity.${fSeverity}`, fSeverity)} selectedOptions={[fSeverity]}
                onOptionSelect={(_, d) => setFSeverity(d.optionValue ?? 'all')} style={{ minWidth: 110 }}>
                <Option value="all">Toutes</Option>
                <Option value="critical">{t('severity.critical', 'Critique')}</Option>
                <Option value="high">{t('severity.high', 'Élevée')}</Option>
                <Option value="medium">{t('severity.medium', 'Moyenne')}</Option>
                <Option value="low">{t('severity.low', 'Faible')}</Option>
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Tous"
                value={fStatus === 'all' ? 'Tous' : fStatus === 'not-scanned' ? 'Non scannés' : t(`controls.filter.${fStatus}Plural`, fStatus)}
                selectedOptions={[fStatus]} onOptionSelect={(_, d) => setFStatus(d.optionValue ?? 'all')} style={{ minWidth: 130 }}>
                <Option value="all">Tous</Option>
                <Option value="pass">Conformes</Option>
                <Option value="warning">À améliorer</Option>
                <Option value="fail">Non conformes</Option>
                <Option value="not-scanned">Non scannés</Option>
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell><Input size="small" placeholder="CIS, ISO, NIS2…" value={fReference} onChange={(_, d) => setFReference(d.value)} style={{ width: '100%' }} /></TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((c) => {
            const st = statusById.get(c.id);
            return (
              <TableRow
                key={c.id}
                data-nis365-clickable-row
                onClick={() => onNavigate(c.id)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(c.id); } }}
              >
                <TableCell><span style={{ color: tokens.colorBrandForeground1, fontWeight: 600 }}>{c.id}</span></TableCell>
                <TableCell><Badge appearance="outline">{c.family}</Badge></TableCell>
                <TableCell>
                  <Body1 style={{ fontWeight: 600 }}>{c.title}</Body1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                    {c.description.slice(0, 100)}{c.description.length > 100 ? '...' : ''}
                  </Caption1>
                </TableCell>
                <TableCell><SeverityBadge severity={c.severity} /></TableCell>
                <TableCell>
                  {st
                    ? <StatusBadge status={st as never} />
                    : <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('controls.list.notScanned')}</Caption1>}
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {c.references.slice(0, 3).map((r, i) => (
                      <FrameworkInlineText key={i} reference={r} />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function AutoTab({ scan, onNavigate }: {
  scan: ReturnType<typeof useLatestScan>;
  onNavigate: (controlId: string) => void;
}) {
  const { t } = useTranslation();
  const resultByControlId = useMemo(() => {
    const map = new Map<string, string>();
    scan?.results.forEach((r) => map.set(r.controlId, r.status));
    return map;
  }, [scan]);

  // v2026.06.168 - Filtres par colonne (style Excel)
  const [filterId,        setFilterId]        = useState('');
  const [filterFamily,    setFilterFamily]    = useState<string>('all');     // 'all' | famille
  const [filterTitle,     setFilterTitle]     = useState('');
  const [filterSeverity,  setFilterSeverity]  = useState<string>('all');     // 'all' | severity
  const [filterStatus,    setFilterStatus]    = useState<string>('all');     // 'all' | pass/fail/warning/manual/not-scanned
  const [filterReference, setFilterReference] = useState('');

  const allFamilies = useMemo(() => Array.from(new Set(controls.map((c) => c.family))).sort(), []);

  const rows = useMemo(() => {
    return controls.filter((c) => {
      if (filterId        && !c.id.toLowerCase().includes(filterId.toLowerCase())) return false;
      if (filterFamily !== 'all' && c.family !== filterFamily) return false;
      if (filterTitle     && !c.title.toLowerCase().includes(filterTitle.toLowerCase())) return false;
      if (filterSeverity !== 'all' && c.severity !== filterSeverity) return false;
      if (filterStatus !== 'all') {
        const st = resultByControlId.get(c.id);
        if (filterStatus === 'not-scanned') {
          if (st) return false;
        } else if (st !== filterStatus) return false;
      }
      if (filterReference && !c.references.some((r) => `${r.framework} ${r.ref} ${r.label}`.toLowerCase().includes(filterReference.toLowerCase()))) return false;
      return true;
    });
  }, [filterId, filterFamily, filterTitle, filterSeverity, filterStatus, filterReference, resultByControlId]);

  const activeFilters = (filterId ? 1 : 0) + (filterFamily !== 'all' ? 1 : 0) + (filterTitle ? 1 : 0) +
                        (filterSeverity !== 'all' ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0) +
                        (filterReference ? 1 : 0);
  const resetAllFilters = () => {
    setFilterId(''); setFilterFamily('all'); setFilterTitle('');
    setFilterSeverity('all'); setFilterStatus('all'); setFilterReference('');
  };

  return (
    <Card>
      <style>{`
        tr[data-nis365-clickable-row] {
          cursor: pointer;
          transition: background-color 0.12s ease;
        }
        tr[data-nis365-clickable-row]:hover {
          background-color: ${tokens.colorNeutralBackground1Hover};
        }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {rows.length} / {controls.length}
        </Caption1>
        {activeFilters > 0 && (
          <Button size="small" appearance="subtle" onClick={resetAllFilters}>
            Réinitialiser les filtres ({activeFilters})
          </Button>
        )}
      </div>
      <Table arial-label={t('complianceManager.tabs.auto')}>
        <TableHeader>
          {/* Ligne 1 : titres */}
          <TableRow>
            <TableHeaderCell>{t('controls.list.id')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.table.family')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.list.control')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.table.severity')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.filter.status')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.list.references')}</TableHeaderCell>
          </TableRow>
          {/* Ligne 2 : filtres par colonne (style Excel) */}
          <TableRow>
            <TableHeaderCell>
              <Input size="small" placeholder="ID…" value={filterId}
                onChange={(_, d) => setFilterId(d.value)} style={{ width: '100%', minWidth: 90 }} />
            </TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Toutes"
                value={filterFamily === 'all' ? 'Toutes' : filterFamily}
                selectedOptions={[filterFamily]}
                onOptionSelect={(_, d) => setFilterFamily(d.optionValue ?? 'all')}
                style={{ minWidth: 110 }}>
                <Option value="all">Toutes</Option>
                {allFamilies.map((f) => <Option key={f} value={f}>{f}</Option>)}
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell>
              <Input size="small" placeholder="Rechercher titre…" value={filterTitle}
                contentBefore={<Search20Regular />}
                onChange={(_, d) => setFilterTitle(d.value)} style={{ width: '100%' }} />
            </TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Toutes"
                value={filterSeverity === 'all' ? 'Toutes' : t(`severity.${filterSeverity}`, filterSeverity)}
                selectedOptions={[filterSeverity]}
                onOptionSelect={(_, d) => setFilterSeverity(d.optionValue ?? 'all')}
                style={{ minWidth: 110 }}>
                <Option value="all">Toutes</Option>
                <Option value="critical">{t('severity.critical', 'Critique')}</Option>
                <Option value="high">{t('severity.high', 'Élevée')}</Option>
                <Option value="medium">{t('severity.medium', 'Moyenne')}</Option>
                <Option value="low">{t('severity.low', 'Faible')}</Option>
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Tous"
                value={filterStatus === 'all' ? 'Tous'
                     : filterStatus === 'not-scanned' ? 'Non scannés'
                     : t(`controls.filter.${filterStatus}Plural`, filterStatus)}
                selectedOptions={[filterStatus]}
                onOptionSelect={(_, d) => setFilterStatus(d.optionValue ?? 'all')}
                style={{ minWidth: 130 }}>
                <Option value="all">Tous</Option>
                <Option value="pass">{t('controls.filter.passPlural', 'Conformes')}</Option>
                <Option value="warning">{t('controls.filter.warnPlural', 'À améliorer')}</Option>
                <Option value="fail">{t('controls.filter.failPlural', 'Non conformes')}</Option>
                <Option value="manual">{t('controls.filter.manualPlural', 'Manuels')}</Option>
                <Option value="not-scanned">Non scannés</Option>
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell>
              <Input size="small" placeholder="NIS2, CIS, ISO…" value={filterReference}
                onChange={(_, d) => setFilterReference(d.value)} style={{ width: '100%' }} />
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((c) => {
            const statusForControl = resultByControlId.get(c.id);
            return (
              <TableRow
                key={c.id}
                data-nis365-clickable-row
                onClick={() => onNavigate(c.id)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigate(c.id);
                  }
                }}
              >
                <TableCell><span style={{ color: tokens.colorBrandForeground1, fontWeight: 600 }}>{c.id}</span></TableCell>
                <TableCell><FamilyBadge family={c.family} /></TableCell>
                <TableCell>
                  <Body1 style={{ fontWeight: 600 }}>{c.title}</Body1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                    {c.description.slice(0, 100)}{c.description.length > 100 ? '...' : ''}
                  </Caption1>
                </TableCell>
                <TableCell><SeverityBadge severity={c.severity} /></TableCell>
                <TableCell>
                  {statusForControl
                    ? <StatusBadge status={statusForControl as any} />
                    : <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('controls.list.notScanned')}</Caption1>}
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {c.references.slice(0, 3).map((r, i) => (
                      <FrameworkInlineText key={i} reference={r} />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

// ============================================================================
//                              MANUAL TAB (Governance)
// ============================================================================
function ManualTab({ decisions, onEdit, canEdit }: {
  decisions: Record<string, ManualDecision>;
  onEdit: (c: GovernanceControlDefinition) => void;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  // v2026.06.168 - Filtres par colonne
  const [fId, setFId]             = useState('');
  const [fFamily, setFFamily]     = useState<GovernanceFamily | 'all'>('all');
  const [fTitle, setFTitle]       = useState('');
  const [fSeverity, setFSeverity] = useState<string>('all');
  const [fDecision, setFDecision] = useState<string>('all');
  const [fReference, setFReference] = useState('');
  const activeFilters = (fId ? 1 : 0) + (fFamily !== 'all' ? 1 : 0) + (fTitle ? 1 : 0) + (fSeverity !== 'all' ? 1 : 0) + (fDecision !== 'all' ? 1 : 0) + (fReference ? 1 : 0);
  const resetAll = () => { setFId(''); setFFamily('all'); setFTitle(''); setFSeverity('all'); setFDecision('all'); setFReference(''); };

  const filtered = useMemo(() => {
    return MANUAL_GOVERNANCE_CONTROLS.filter((c) => {
      if (fId && !c.id.toLowerCase().includes(fId.toLowerCase())) return false;
      if (fFamily !== 'all' && c.family !== fFamily) return false;
      if (fTitle && !c.title.toLowerCase().includes(fTitle.toLowerCase())) return false;
      if (fSeverity !== 'all' && c.severity !== fSeverity) return false;
      if (fDecision !== 'all') {
        const dec = decisions[c.id]?.decision ?? 'pending';
        if (dec !== fDecision) return false;
      }
      if (fReference && !c.references.some((r) => `${r.framework} ${r.ref} ${r.label}`.toLowerCase().includes(fReference.toLowerCase()))) return false;
      return true;
    });
  }, [fId, fFamily, fTitle, fSeverity, fDecision, fReference, decisions]);

  return (
    <Card>
      <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Body1 style={{ color: tokens.colorNeutralForeground2, flex: 1 }}>
          {t('complianceManager.manual.description')}
        </Body1>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {filtered.length} / {MANUAL_GOVERNANCE_CONTROLS.length}
        </Caption1>
        {activeFilters > 0 && (
          <Button size="small" appearance="subtle" onClick={resetAll}>Réinitialiser les filtres ({activeFilters})</Button>
        )}
      </div>

      <style>{`
        tr[data-nis365-clickable-row] {
          cursor: pointer;
          transition: background-color 0.12s ease;
        }
        tr[data-nis365-clickable-row]:hover {
          background-color: ${tokens.colorNeutralBackground1Hover};
        }
      `}</style>
      <Table arial-label={t('complianceManager.tabs.manual')}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>{t('controls.list.id')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.table.family')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.list.control')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.table.severity')}</TableHeaderCell>
            <TableHeaderCell>{t('complianceManager.manual.decision')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.list.references')}</TableHeaderCell>
          </TableRow>
          {/* v2026.06.168 - filtres par colonne */}
          <TableRow>
            <TableHeaderCell><Input size="small" placeholder="ID…" value={fId} onChange={(_, d) => setFId(d.value)} style={{ width: '100%', minWidth: 90 }} /></TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Toutes"
                value={fFamily === 'all' ? 'Toutes' : t(`complianceManager.governanceFamily.${fFamily}`, fFamily)}
                selectedOptions={[fFamily]} onOptionSelect={(_, d) => setFFamily((d.optionValue as GovernanceFamily | 'all') ?? 'all')} style={{ minWidth: 130 }}>
                <Option value="all">Toutes</Option>
                {GOVERNANCE_FAMILY_ORDER.map((f) => <Option key={f} value={f}>{t(`complianceManager.governanceFamily.${f}`, f)}</Option>)}
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell><Input size="small" placeholder="Rechercher titre…" value={fTitle} contentBefore={<Search20Regular />} onChange={(_, d) => setFTitle(d.value)} style={{ width: '100%' }} /></TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Toutes" value={fSeverity === 'all' ? 'Toutes' : t(`severity.${fSeverity}`, fSeverity)} selectedOptions={[fSeverity]}
                onOptionSelect={(_, d) => setFSeverity(d.optionValue ?? 'all')} style={{ minWidth: 110 }}>
                <Option value="all">Toutes</Option>
                <Option value="critical">{t('severity.critical', 'Critique')}</Option>
                <Option value="high">{t('severity.high', 'Élevée')}</Option>
                <Option value="medium">{t('severity.medium', 'Moyenne')}</Option>
                <Option value="low">{t('severity.low', 'Faible')}</Option>
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Toutes"
                value={fDecision === 'all' ? 'Toutes' : fDecision} selectedOptions={[fDecision]}
                onOptionSelect={(_, d) => setFDecision(d.optionValue ?? 'all')} style={{ minWidth: 130 }}>
                <Option value="all">Toutes</Option>
                <Option value="conforme">Conforme</Option>
                <Option value="pending">À évaluer</Option>
                <Option value="fail">Non conforme</Option>
                <Option value="na">Non appl.</Option>
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell><Input size="small" placeholder="NIS2, ISO…" value={fReference} onChange={(_, d) => setFReference(d.value)} style={{ width: '100%' }} /></TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((c) => {
            const decision = decisions[c.id];
            return (
              <TableRow
                key={c.id}
                data-nis365-clickable-row
                onClick={() => canEdit && onEdit(c)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onEdit(c);
                  }
                }}
              >
                <TableCell><span style={{ color: tokens.colorBrandForeground1, fontWeight: 600 }}>{c.id}</span></TableCell>
                <TableCell><GovernanceFamilyBadge family={c.family} /></TableCell>
                <TableCell>
                  <Body1 style={{ fontWeight: 600 }}>{c.title}</Body1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                    {c.description.slice(0, 100)}{c.description.length > 100 ? '...' : ''}
                  </Caption1>
                </TableCell>
                <TableCell><SeverityBadge severity={c.severity} /></TableCell>
                <TableCell><DecisionBadge decision={decision?.decision} /></TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {c.references.slice(0, 3).map((r, i) => (
                      <FrameworkInlineText key={i} reference={r} />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

// ============================================================================
//                              GLOBAL TAB (combine)
// ============================================================================
// ============================================================================
//                              GLOBAL TAB (M365 + Manual unified)
// v2026.06.168 - filtres par colonne (ID, Source, Titre, Criticité, Statut, Réf.)
// ============================================================================
function GlobalTab({ scan, decisions, onEditManual, onNavigateAuto, canEdit }: {
  scan: ReturnType<typeof useLatestScan>;
  decisions: Record<string, ManualDecision>;
  onEditManual: (c: GovernanceControlDefinition) => void;
  onNavigateAuto: (id: string) => void;
  canEdit: boolean;
}) {
  void canEdit;
  const { t } = useTranslation();
  const { tenant } = useActiveTenant();

  // M365 scan results
  const resultByControlId = useMemo(() => {
    const map = new Map<string, string>();
    scan?.results.forEach((r) => map.set(r.controlId, r.status));
    return map;
  }, [scan]);

  // v2026.06.169 - Azure scan results (1ère connexion)
  const azureConnQ = useQuery({ queryKey: ['azure-connections'], queryFn: () => azureConnectionsApi.list(), staleTime: 60_000, retry: false });
  const azureConnId = azureConnQ.data?.connections?.[0]?.id;
  const azureScanQ = useQuery({
    queryKey: ['azure-latest', azureConnId],
    queryFn:  () => azureScansApi.latest(azureConnId!),
    enabled:  !!azureConnId, staleTime: 10_000, retry: false,
  });
  const azureStatusById = useMemo(() => {
    const m = new Map<string, string>();
    (azureScanQ.data?.results ?? []).forEach((r) => m.set(r.controlId, r.status));
    return m;
  }, [azureScanQ.data]);

  // v2026.06.169 - Custom controls (localStorage)
  const [customCtrls, setCustomCtrls] = useState<Array<{ id: string; title: string; condition?: string; framework?: string; reference?: string; status: string }>>([]);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('nis365-custom-controls-' + tenant.id);
      const arr = raw ? JSON.parse(raw) : [];
      setCustomCtrls(Array.isArray(arr) ? arr : []);
    } catch { setCustomCtrls([]); }
  }, [tenant.id]);

  type SourceKey = 'auto-m365' | 'auto-azure' | 'manual-gov' | 'manual-org' | 'custom';
  type GlobalRow = {
    source:     SourceKey;
    id:         string;
    title:      string;
    description: string;
    severity:   import('../domain/types').Severity;
    references: import('../domain/types').ControlReference[];
    family:     string;
    status?:    string;          // pour auto
    decision?:  ManualDecisionValue; // pour manuel
    customStatus?: string;       // pour custom
  };

  // v2026.06.169 - "Gouvernance" vs "Org" : partition fonctionnelle des familles manuelles
  const GOV_FAMILIES: GovernanceFamily[] = ['governance', 'hr', 'training', 'asset', 'crypto', 'incident'];
  // ORG = physical + network + continuity + supply
  function manualSource(family: GovernanceFamily): 'manual-gov' | 'manual-org' {
    return GOV_FAMILIES.includes(family) ? 'manual-gov' : 'manual-org';
  }

  const allRows: GlobalRow[] = useMemo(() => {
    const m365: GlobalRow[] = controls.map((c) => ({
      source: 'auto-m365', id: c.id, title: c.title, description: c.description,
      severity: c.severity, references: c.references ?? [], family: c.family,
      status: resultByControlId.get(c.id),
    }));
    const azure: GlobalRow[] = AZURE_CONTROLS.map((c) => ({
      source: 'auto-azure', id: c.id, title: c.title, description: c.description,
      severity: c.severity, references: c.references ?? [], family: c.family,
      status: azureStatusById.get(c.id),
    }));
    const manual: GlobalRow[] = MANUAL_GOVERNANCE_CONTROLS.map((c) => ({
      source: manualSource(c.family), id: c.id, title: c.title, description: c.description,
      severity: c.severity, references: c.references ?? [], family: c.family,
      decision: decisions[c.id]?.decision,
    }));
    const custom: GlobalRow[] = customCtrls.map((c) => ({
      source: 'custom', id: c.id, title: c.title, description: c.condition ?? '',
      severity: 'medium' as const,
      references: c.reference
        ? [{ framework: (c.framework?.toUpperCase() ?? 'AUTRE') as never, ref: c.reference, label: '' }]
        : [],
      family: 'Custom',
      customStatus: c.status,
    }));
    return [...m365, ...azure, ...manual, ...custom];
  }, [resultByControlId, azureStatusById, decisions, customCtrls]);

  // Filtres par colonne
  const [fId, setFId]               = useState('');
  const [fSource, setFSource]       = useState<SourceKey | 'all'>('all');
  const [fTitle, setFTitle]         = useState('');
  const [fSeverity, setFSeverity]   = useState<string>('all');
  const [fStatus, setFStatus]       = useState<string>('all');
  const [fReference, setFReference] = useState('');
  const activeFilters = (fId ? 1 : 0) + (fSource !== 'all' ? 1 : 0) + (fTitle ? 1 : 0)
                       + (fSeverity !== 'all' ? 1 : 0) + (fStatus !== 'all' ? 1 : 0) + (fReference ? 1 : 0);
  const resetAll = () => { setFId(''); setFSource('all'); setFTitle(''); setFSeverity('all'); setFStatus('all'); setFReference(''); };

  // Filtre Référence DÉFENSIF : guard sur references undefined
  function matchReference(refs: import('../domain/types').ControlReference[] | undefined, needle: string): boolean {
    if (!refs || refs.length === 0) return false;
    const q = needle.toLowerCase().trim();
    if (!q) return true;
    return refs.some((r) => {
      const text = `${r?.framework ?? ''} ${r?.ref ?? ''} ${r?.label ?? ''}`.toLowerCase();
      return text.includes(q);
    });
  }

  const rows = useMemo(() => allRows.filter((r) => {
    if (fId && !r.id.toLowerCase().includes(fId.toLowerCase())) return false;
    if (fSource !== 'all' && r.source !== fSource) return false;
    if (fTitle && !r.title.toLowerCase().includes(fTitle.toLowerCase())) return false;
    if (fSeverity !== 'all' && r.severity !== fSeverity) return false;
    if (fStatus !== 'all') {
      if (r.source === 'auto-m365' || r.source === 'auto-azure') {
        if (fStatus === 'not-scanned') { if (r.status) return false; }
        else if (r.status !== fStatus) return false;
      } else if (r.source === 'manual-gov' || r.source === 'manual-org') {
        const dec = r.decision ?? 'pending';
        if (dec !== fStatus) return false;
      } else if (r.source === 'custom') {
        if ((r.customStatus ?? 'pending') !== fStatus) return false;
      }
    }
    if (fReference && !matchReference(r.references, fReference)) return false;
    return true;
  }), [allRows, fId, fSource, fTitle, fSeverity, fStatus, fReference]);

  // Source label pour badges
  const sourceLabel = (s: SourceKey): { label: string; color: 'brand' | 'informative' | 'success' | 'warning' | 'subtle' } => {
    switch (s) {
      case 'auto-m365':   return { label: 'Auto M365',  color: 'brand' };
      case 'auto-azure':  return { label: 'Auto Azure', color: 'informative' };
      case 'manual-gov':  return { label: 'Manuel Gouv.', color: 'success' };
      case 'manual-org':  return { label: 'Manuel Org', color: 'warning' };
      case 'custom':      return { label: 'Custom', color: 'subtle' };
    }
  };

  return (
    <Card>
      <style>{`
        tr[data-nis365-clickable-row] { cursor: pointer; transition: background-color 0.12s ease; }
        tr[data-nis365-clickable-row]:hover { background-color: ${tokens.colorNeutralBackground1Hover}; }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{rows.length} / {allRows.length}</Caption1>
        {activeFilters > 0 && (
          <Button size="small" appearance="subtle" onClick={resetAll}>Réinitialiser les filtres ({activeFilters})</Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>{t('controls.list.id')}</TableHeaderCell>
            <TableHeaderCell>{t('complianceManager.global.sourceColumn', 'Source')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.list.control')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.table.severity')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.filter.status')}</TableHeaderCell>
            <TableHeaderCell>{t('controls.list.references')}</TableHeaderCell>
          </TableRow>
          <TableRow>
            <TableHeaderCell><Input size="small" placeholder="ID…" value={fId} onChange={(_, d) => setFId(d.value)} style={{ width: '100%', minWidth: 90 }} /></TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Toutes"
                value={fSource === 'all' ? 'Toutes' : sourceLabel(fSource).label}
                selectedOptions={[fSource]}
                onOptionSelect={(_, d) => setFSource((d.optionValue as SourceKey | 'all') ?? 'all')}
                style={{ minWidth: 130 }}>
                <Option value="all">Toutes</Option>
                <Option value="auto-m365">Auto M365</Option>
                <Option value="auto-azure">Auto Azure</Option>
                <Option value="manual-gov">Manuel Gouv.</Option>
                <Option value="manual-org">Manuel Org</Option>
                <Option value="custom">Custom</Option>
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell><Input size="small" placeholder="Titre…" value={fTitle} contentBefore={<Search20Regular />} onChange={(_, d) => setFTitle(d.value)} style={{ width: '100%' }} /></TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Toutes" value={fSeverity === 'all' ? 'Toutes' : t(`severity.${fSeverity}`, fSeverity)} selectedOptions={[fSeverity]}
                onOptionSelect={(_, d) => setFSeverity(d.optionValue ?? 'all')} style={{ minWidth: 110 }}>
                <Option value="all">Toutes</Option>
                <Option value="critical">{t('severity.critical', 'Critique')}</Option>
                <Option value="high">{t('severity.high', 'Élevée')}</Option>
                <Option value="medium">{t('severity.medium', 'Moyenne')}</Option>
                <Option value="low">{t('severity.low', 'Faible')}</Option>
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell>
              <Dropdown size="small" placeholder="Tous" value={fStatus === 'all' ? 'Tous' : fStatus} selectedOptions={[fStatus]}
                onOptionSelect={(_, d) => setFStatus(d.optionValue ?? 'all')} style={{ minWidth: 130 }}>
                <Option value="all">Tous</Option>
                <Option value="pass">Conformes (auto)</Option>
                <Option value="warning">À améliorer (auto)</Option>
                <Option value="fail">Non conformes (auto)</Option>
                <Option value="not-scanned">Non scannés (auto)</Option>
                <Option value="conforme">Conforme (manuel/custom)</Option>
                <Option value="pending">À évaluer (manuel/custom)</Option>
                <Option value="na">N/A (manuel)</Option>
              </Dropdown>
            </TableHeaderCell>
            <TableHeaderCell><Input size="small" placeholder="NIS2, ISO, CyFun…" value={fReference} onChange={(_, d) => setFReference(d.value)} style={{ width: '100%' }} /></TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const src = sourceLabel(r.source);
            return (
              <TableRow
                key={`${r.source}-${r.id}`}
                data-nis365-clickable-row
                onClick={() => {
                  if (r.source === 'auto-m365' || r.source === 'auto-azure') onNavigateAuto(r.id);
                  else if (r.source === 'manual-gov' || r.source === 'manual-org') {
                    const c = MANUAL_GOVERNANCE_CONTROLS.find((x) => x.id === r.id);
                    if (c) onEditManual(c);
                  }
                }}
              >
                <TableCell><span style={{ color: tokens.colorBrandForeground1, fontWeight: 600 }}>{r.id}</span></TableCell>
                <TableCell><Badge appearance="outline" color={src.color}>{src.label}</Badge></TableCell>
                <TableCell>
                  <Body1 style={{ fontWeight: 600 }}>{r.title}</Body1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                    {r.description.slice(0, 100)}{r.description.length > 100 ? '...' : ''}
                  </Caption1>
                </TableCell>
                <TableCell><SeverityBadge severity={r.severity} /></TableCell>
                <TableCell>
                  {(r.source === 'auto-m365' || r.source === 'auto-azure')
                    ? (r.status
                        ? <StatusBadge status={r.status as never} />
                        : <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('controls.list.notScanned')}</Caption1>)
                    : r.source === 'custom'
                      ? <Badge appearance="filled" style={{
                          background: r.customStatus === 'conforme' ? '#DCF0E5' : r.customStatus === 'fail' ? '#FBE7E7' : '#EEEEEE',
                          color:      r.customStatus === 'conforme' ? '#1F7A45' : r.customStatus === 'fail' ? '#B82329' : '#7A7A7A',
                          fontWeight: 600,
                        }}>{r.customStatus ?? 'pending'}</Badge>
                      : <DecisionBadge decision={r.decision} />}
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {(r.references ?? []).slice(0, 3).map((ref, i) => (
                      <FrameworkInlineText key={i} reference={ref} />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

// ============================================================================
// Helpers visuels
// ============================================================================
function AttachmentPreview({ attachment, onRemove, readOnly }: { attachment: EvidenceAttachment; onRemove?: () => void; readOnly?: boolean }) {
  const sizeKb = Math.round(attachment.size / 1024);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <a href={attachment.dataUrl} download={attachment.name}
         style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px',
                  background: tokens.colorNeutralBackground2, borderRadius: 4, fontSize: 12,
                  color: tokens.colorBrandForeground1, textDecoration: 'none' }}>
        <Attach20Regular style={{ width: 14, height: 14 }} />
        {attachment.name} <span style={{ color: tokens.colorNeutralForeground3 }}>({sizeKb} Ko)</span>
      </a>
      {onRemove && !readOnly && (
        <Button size="small" appearance="subtle" icon={<Delete20Regular />} onClick={onRemove} />
      )}
    </span>
  );
}

function GovernanceFamilyBadge({ family }: { family: GovernanceFamily }) {
  const color = GOVERNANCE_FAMILY_COLORS?.[family] ?? { fg: '#4A4A6E', bg: '#E9E9F2' };
  return (
    <Badge appearance="filled" style={{ background: color.bg, color: color.fg, fontWeight: 600 }}>
      {family}
    </Badge>
  );
}

function DecisionBadge({ decision }: { decision?: ManualDecisionValue }) {
  const cfg: Record<string, { label: string; bg: string; fg: string }> = {
    conforme: { label: 'Conforme', bg: '#DCF0E5', fg: '#1F7A45' },
    pending:  { label: 'À évaluer', bg: '#EEEEEE', fg: '#7A7A7A' },
    fail:     { label: 'Non conforme', bg: '#FBE7E7', fg: '#B82329' },
    na:       { label: 'Non appl.', bg: '#E5EAF0', fg: '#4A5E72' },
  };
  const c = cfg[decision ?? 'pending'] ?? cfg.pending;
  return <Badge appearance="filled" style={{ background: c.bg, color: c.fg, fontWeight: 600 }}>{c.label}</Badge>;
}
